import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo/Logo';
import { useAuth, type LoginPhase } from '../lib/auth';
import { ApiError } from '../lib/api';
import './Login.css';

// Email + password -> the BFF verifies it against cabana.users -> a passkey ceremony ->
// an httpOnly session. The password on its own opens nothing (Mike, 2026-09-03: every
// staff account must use a passkey); the first sign-in creates one, every later sign-in
// confirms it, and a ceremony that does not complete leaves the person signed out.
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [phase, setPhase] = useState<LoginPhase | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { identity, loading, login } = useAuth();
  const navigate = useNavigate();

  // Already signed in -> go straight to the console.
  if (!loading && identity) return <Navigate to="/" replace />;

  const ready = email.trim() !== '' && password !== '';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await login(email.trim(), password, setPhase);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in. Please try again.');
      setSubmitting(false);
      setPhase(null);
      setPassword('');
    }
  }

  function clearError() {
    if (error) setError('');
  }

  const phaseText =
    phase === 'enroll'
      ? 'Set up a passkey to finish signing in. Your browser will ask you to confirm; staff accounts cannot sign in without one.'
      : phase === 'passkey'
        ? 'Confirm with your passkey to finish signing in.'
        : phase === 'password'
          ? 'Checking your password…'
          : null;

  return (
    <div className="login">
      <div className="login__panel">
        {/* Logo sits on the dark Marine backdrop so the white asset reads. */}
        <Logo />
        <div className="login__card">
          <h1 className="login__title">Sign in</h1>
          <p className="login__hint">Sign in with your Cabana staff account and your passkey.</p>
          <form className="login__form" onSubmit={onSubmit}>
            <label className="login__label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="login__input"
              type="email"
              placeholder="you@cabanafilters.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError();
              }}
              autoComplete="username"
              autoFocus
              disabled={submitting}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'login-error' : undefined}
            />
            <label className="login__label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="login__input"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearError();
              }}
              autoComplete="current-password"
              disabled={submitting}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'login-error' : undefined}
            />
            {phaseText && submitting && (
              <p className="login__hint" id="login-phase" role="status" aria-live="polite">
                {phaseText}
              </p>
            )}
            {error && (
              <p className="login__error" id="login-error" role="alert">
                {error}
              </p>
            )}
            <button className="login__button" type="submit" disabled={!ready || submitting}>
              {submitting ? (phase === 'enroll' ? 'Waiting for your passkey…' : phase === 'passkey' ? 'Confirming…' : 'Signing in…') : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
